import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {
    FiArrowLeft,
    FiArrowRight,
    FiCheck,
    FiCheckCircle,
    FiCopy,
    FiCpu,
    FiDatabase,
    FiEdit3,
    FiTrash2,
    FiUserPlus,
    FiX,
} from "react-icons/fi";

import styles from "./ExamWizardPage.module.css";

import { formatMinutesAsHours, MAX_EXAM_DURATION_MINUTES } from "../../utils/duration";

import {
    createExamBase,
    deleteExamBase,
    deleteQuestionFromExamBase,
    getExamBaseById,
    updateExamBase,
    updateExamBaseWizardStep,
} from "../../services/examBasesApi";
import {getQuestionsByExamBase} from "../../services/questionsApi";
import {createExam} from "../../services/examsApi";
import {createStudent, getStudentNumbers, getStudents} from "../../services/studentsApi";
import {useAuth} from "../../context/AuthContext";
import {assignStudentsToSubject, getSubjectsWithQuestionCount} from "../../services/subjectsApi";

import TopicSelector from "../../components/common/topicSelector/TopicSelector";
import QuestionFormModal from "../examBase/questionFormModal/QuestionFormModal";
import AiQuestionGenerateModal from "../examBase/aiQuestionGenerateModal/aiQuestionGenerateModal";
import SelectFromQuestionBankModal from "../examBase/selectFromQuestionBankModal/SelectFromQuestionBankModal";
import StudentFormModal from "../Students/studentFormModal/StudentFormModal";
import ConfirmDeleteModal from "../../components/common/deleteModal/ConfirmDeleteModal";
import ExamConflictModal from "./examConflictModal/ExamConflictModal";
import Loader from "../../components/common/Loader/Loader";
import QuestionContent from "../../components/common/QuestionContent/QuestionContent";
import {formatDateTime} from "../../utils/formatDateTime";

const DEFAULT_DURATION = 30;
const DEFAULT_PASS_RATE = 60;
const PHONE_LOCAL_REGEX = /^[1-9]\d{8}$/;
// Step 4 pages the student list this many rows at a time as it is scrolled.
const STUDENT_PAGE_SIZE = 20;

const EMPTY_STUDENT = {
    studentNumber: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    nationalId: "",
    birthDate: "",
    gender: "",
    address: "",
};

// datetime-local <-> ISO-with-offset (mirrors the helpers used by the advanced scheduling page).
const toIsoWithOffset = (localInputValue) => {
    if (!localInputValue) return "";
    const d = new Date(localInputValue);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const tzMin = -d.getTimezoneOffset();
    const sign = tzMin >= 0 ? "+" : "-";
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes()
    )}:${pad(d.getSeconds())}${sign}${pad(Math.floor(Math.abs(tzMin) / 60))}:${pad(Math.abs(tzMin) % 60)}`;
};

// Default start = ~10 minutes from now (the backend requires a future start), rounded to the minute.
const defaultStartLocal = () => {
    const d = new Date(Date.now() + 10 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const errMsg = (e, fallback) =>
    e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;

const WizardProgress = ({step, labels}) => (
    <div className={styles.progress}>
        {labels.map((label, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
                <div className={styles.progressStep} key={n}>
                    {i > 0 && (
                        <span
                            className={`${styles.progressConnector} ${step >= n ? styles.progressConnectorDone : ""}`}
                        />
                    )}
                    <span
                        className={`${styles.progressDot} ${active ? styles.progressDotActive : ""} ${
                            done ? styles.progressDotDone : ""
                        }`}
                    >
                        {done ? <FiCheck size={16}/> : n}
                    </span>
                    <span className={`${styles.progressLabel} ${active ? styles.progressLabelActive : ""}`}>{label}</span>
                </div>
            );
        })}
    </div>
);

const ExamWizardPage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {examBaseId: examBaseIdParam} = useParams();
    const {user} = useAuth();
    // View-only roles (TEACHER) can build/schedule exams but cannot create students inline.
    const canManageStudents = (user?.roles || []).some((r) => ["ADMIN", "OWNER", "COORDINATOR"].includes(r));

    const [bootLoading, setBootLoading] = useState(Boolean(examBaseIdParam));
    const [step, setStep] = useState(1);
    const [done, setDone] = useState(false);
    const [advancing, setAdvancing] = useState(false);
    const [error, setError] = useState("");
    const [softError, setSoftError] = useState("");

    const [examBaseId, setExamBaseId] = useState(null);

    // Step 1
    const [examName, setExamName] = useState("");
    // Multi-topic templates: the leaf topics (possibly across subjects) this exam draws from.
    // [{ id, title, parentName }]. The first is the primary topic.
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [subjectOptions, setSubjectOptions] = useState([]);

    // Step 3 settings (also seed the template's defaults at creation time in step 1)
    const [durationMinutes, setDurationMinutes] = useState(String(DEFAULT_DURATION));
    const [passRate, setPassRate] = useState(String(DEFAULT_PASS_RATE));

    // Step 2
    const [questions, setQuestions] = useState([]);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [aiOpen, setAiOpen] = useState(false);
    const [bankOpen, setBankOpen] = useState(false);

    // Step 4
    const [startLocal, setStartLocal] = useState(defaultStartLocal);
    // Conflict popup: list of {studentNumber, firstName, lastName, examTitle, scheduledStartTime,
    // scheduledEndTime} the backend returns when chosen students are already booked at this time.
    const [scheduleConflicts, setScheduleConflicts] = useState(null);
    const startTimeRef = useRef(null);
    const [note, setNote] = useState("");
    const [proctored, setProctored] = useState(false);
    // Students are paged from the server as the list is scrolled, and searched server-side — the step
    // used to pull 1000 rows in one request just to filter them in the browser.
    const [students, setStudents] = useState([]);
    const [studentsPage, setStudentsPage] = useState(0);
    const [studentsHasMore, setStudentsHasMore] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [studentsTotal, setStudentsTotal] = useState(0);
    const [selected, setSelected] = useState(() => new Set());
    // Names of the selected students, so a selection stays visible (and removable) after a search or
    // scroll moves that student out of the loaded page.
    const [selectedNames, setSelectedNames] = useState(() => new Map());
    const [selectAllBusy, setSelectAllBusy] = useState(false);
    const [studentSearch, setStudentSearch] = useState("");
    const [studentSearchTerm, setStudentSearchTerm] = useState("");
    const [studentModalOpen, setStudentModalOpen] = useState(false);
    const [studentForm, setStudentForm] = useState(EMPTY_STUDENT);
    const [studentSaving, setStudentSaving] = useState(false);
    const [studentFormError, setStudentFormError] = useState("");

    const [discardOpen, setDiscardOpen] = useState(false);
    const [discarding, setDiscarding] = useState(false);

    const [publishedInfo, setPublishedInfo] = useState(null);

    const stepLabels = [
        t("pages.examWizard.steps.name"),
        t("pages.examWizard.steps.questions"),
        t("pages.examWizard.steps.settings"),
        t("pages.examWizard.steps.schedule"),
    ];

    // ── Initial load ───────────────────────────────────────────────────────
    const loadSubjectOptions = useCallback(async () => {
        try {
            const data = await getSubjectsWithQuestionCount();
            const list = Array.isArray(data) ? data : data?.content || [];
            setSubjectOptions(
                list.map((s) => ({
                    id: s.id,
                    title: s.title || s.name,
                    questionCount: s.questionCount,
                    parentId: s.parentId,
                    parentName: s.parentName,
                }))
            );
        } catch {
            // Non-fatal: TopicSelector can still create a new subject inline.
        }
    }, []);

    const resumeDraft = useCallback(
        async (id) => {
            setBootLoading(true);
            try {
                const dto = await getExamBaseById(id);
                setExamBaseId(id);
                setExamName(dto.title || "");
                setSelectedTopics(
                    Array.isArray(dto.subjects) && dto.subjects.length > 0
                        ? dto.subjects.map((s) => ({id: s.id, title: s.title, parentName: s.parentName}))
                        : dto.subjectId ? [{id: dto.subjectId, title: dto.subjectTitle, parentName: null}] : []
                );
                setDurationMinutes(dto.durationMinutes ? String(dto.durationMinutes) : String(DEFAULT_DURATION));
                setPassRate(dto.passRate ? String(dto.passRate) : String(DEFAULT_PASS_RATE));
                const resumeStep = dto.wizardStep && dto.wizardStep >= 1 && dto.wizardStep <= 4 ? dto.wizardStep : 2;
                setStep(resumeStep);
            } catch (e) {
                toast.error(errMsg(e, t("pages.examWizard.resumeError")));
                navigate("/exams/base");
            } finally {
                setBootLoading(false);
            }
        },
        [navigate, t]
    );

    useEffect(() => {
        loadSubjectOptions();
        if (examBaseIdParam) {
            resumeDraft(Number(examBaseIdParam));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Step 2 data ────────────────────────────────────────────────────────
    const fetchQuestions = useCallback(async () => {
        if (!examBaseId) return;
        setQuestionsLoading(true);
        try {
            const raw = await getQuestionsByExamBase(examBaseId);
            setQuestions(Array.isArray(raw) ? raw : raw?.content || []);
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.questionsLoadError")));
        } finally {
            setQuestionsLoading(false);
        }
    }, [examBaseId, t]);

    useEffect(() => {
        if (step === 2 && examBaseId) fetchQuestions();
    }, [step, examBaseId, fetchQuestions]);

    // ── Step 4 data ────────────────────────────────────────────────────────
    // One screenful at a time, searched server-side. Nothing here loads the whole roster: the old
    // pageSize=1000 request grew with the tenant and blocked the step from rendering.
    const fetchStudentsPage = useCallback(async (page, term) => {
        setStudentsLoading(true);
        try {
            // `page` stays 0-based here because the append/replace logic below keys off page 0;
            // the API itself is 1-based.
            const data = await getStudents({pageNo: page + 1, pageSize: STUDENT_PAGE_SIZE, search: term});
            const rows = Array.isArray(data?.content) ? data.content : [];
            const total = Number(data?.totalElements) || 0;
            setStudentsTotal(total);
            setStudents((prev) => {
                // page 0 replaces (new search); later pages append.
                const next = page === 0 ? rows : [...prev, ...rows];
                setStudentsHasMore(next.length < total);
                return next;
            });
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.studentsLoadError")));
            setStudentsHasMore(false);
        } finally {
            setStudentsLoading(false);
        }
    }, [t]);

    // Debounce the search box so typing doesn't fire a request per keystroke.
    useEffect(() => {
        const id = setTimeout(() => setStudentSearchTerm(studentSearch.trim()), 350);
        return () => clearTimeout(id);
    }, [studentSearch]);

    // A new search term restarts at page 0; scrolling bumps studentsPage.
    useEffect(() => {
        if (step !== 4 || !examBaseId) return;
        setStudentsPage(0);
        fetchStudentsPage(0, studentSearchTerm);
    }, [step, examBaseId, studentSearchTerm, fetchStudentsPage]);

    useEffect(() => {
        if (step !== 4 || !examBaseId || studentsPage === 0) return;
        fetchStudentsPage(studentsPage, studentSearchTerm);
        // studentSearchTerm intentionally omitted: a term change resets the page above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentsPage]);

    // Infinite scroll: pull the next page as the list nears its bottom.
    const handleStudentsScroll = (e) => {
        if (studentsLoading || !studentsHasMore) return;
        const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 80) {
            setStudentsPage((p) => p + 1);
        }
    };

    // ── Navigation ─────────────────────────────────────────────────────────
    const goBack = () => {
        setError("");
        setSoftError("");
        if (step > 1) setStep(step - 1);
    };

    // Step 1 multi-topic helpers — TopicSelector is driven as an "add" control (value stays ""),
    // appending each pick/created topic to the chip list; dedupe so re-picking is a no-op.
    const addTopic = (id, topic) => {
        if (!id) return;
        const numId = Number(id);
        setSelectedTopics((prev) =>
            prev.some((tp) => Number(tp.id) === numId)
                ? prev
                : [...prev, {id: numId, title: topic?.title || "", parentName: topic?.parentName || null}]
        );
    };

    const removeTopic = (id) => {
        const numId = Number(id);
        setSelectedTopics((prev) => prev.filter((tp) => Number(tp.id) !== numId));
    };

    const handleStep1Next = async () => {
        setError("");
        if (!examName.trim()) {
            setSoftError(t("pages.examWizard.step1.nameRequired"));
            return;
        }
        if (selectedTopics.length === 0) {
            setSoftError(t("pages.examWizard.step1.topicsRequired"));
            return;
        }
        setSoftError("");
        setAdvancing(true);
        try {
            if (!examBaseId) {
                const resp = await createExamBase({
                    title: examName.trim(),
                    durationMinutes: Number(durationMinutes) || DEFAULT_DURATION,
                    passRate: Number(passRate) || DEFAULT_PASS_RATE,
                    subjectIds: selectedTopics.map((tp) => tp.id),
                    wizardStep: 2,
                });
                const newId = resp?.data?.id;
                if (!newId) throw new Error(t("pages.examWizard.createError"));
                setExamBaseId(newId);
            } else {
                // Coming back to step 1 lets the user change the topic set, so re-send it. The backend
                // replaces the template's topics but keeps already-added questions — only the pool the
                // next step can draw from (and student eligibility) changes.
                await updateExamBase({
                    id: examBaseId,
                    title: examName.trim(),
                    durationMinutes: Number(durationMinutes) || DEFAULT_DURATION,
                    passRate: Number(passRate) || DEFAULT_PASS_RATE,
                    subjectIds: selectedTopics.map((tp) => tp.id),
                });
                await updateExamBaseWizardStep(examBaseId, 2);
            }
            setStep(2);
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.createError")));
        } finally {
            setAdvancing(false);
        }
    };

    const handleStep2Next = async () => {
        setError("");
        if (questions.length === 0) {
            setSoftError(t("pages.examWizard.step2.needOne"));
            return;
        }
        setSoftError("");
        setAdvancing(true);
        try {
            await updateExamBaseWizardStep(examBaseId, 3);
            setStep(3);
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.saveError")));
        } finally {
            setAdvancing(false);
        }
    };

    const handleStep3Next = async (useDefaults) => {
        setError("");
        const dur = useDefaults ? DEFAULT_DURATION : Number(durationMinutes);
        const pr = useDefaults ? DEFAULT_PASS_RATE : Number(passRate);
        if (!Number.isFinite(dur) || dur < 1) {
            setSoftError(t("pages.examWizard.step3.durationInvalid"));
            return;
        }
        if (dur > 300) {
            setSoftError(t("pages.examWizard.step3.durationTooLong"));
            return;
        }
        if (!Number.isFinite(pr) || pr < 1 || pr > 100) {
            setSoftError(t("pages.examWizard.step3.passRateInvalid"));
            return;
        }
        setSoftError("");
        if (useDefaults) {
            setDurationMinutes(String(DEFAULT_DURATION));
            setPassRate(String(DEFAULT_PASS_RATE));
        }
        setAdvancing(true);
        try {
            await updateExamBase({id: examBaseId, title: examName.trim(), durationMinutes: dur, passRate: pr});
            await updateExamBaseWizardStep(examBaseId, 4);
            setStep(4);
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.saveError")));
        } finally {
            setAdvancing(false);
        }
    };

    const handlePublish = async () => {
        setError("");
        const assigned = [...selected];
        if (assigned.length === 0) {
            setSoftError(t("pages.examWizard.step4.needStudent"));
            return;
        }
        if (!startLocal) {
            setSoftError(t("pages.examWizard.step4.needStart"));
            return;
        }
        setSoftError("");
        setAdvancing(true);
        try {
            // Enroll the selected students into the primary topic — union eligibility means that is
            // enough to make them eligible for a multi-topic exam, and the backend silently skips
            // anyone already enrolled. We no longer pre-fetch the eligible roster just to diff
            // against it: that was a second unbounded load of every eligible student.
            if (assigned.length > 0 && selectedTopics.length > 0) {
                await assignStudentsToSubject(Number(selectedTopics[0].id), assigned);
            }
            await createExam({
                examBaseId: Number(examBaseId),
                assignedStudentNumbers: assigned,
                scheduledStartTime: toIsoWithOffset(startLocal),
                durationMinutes: Number(durationMinutes) || DEFAULT_DURATION,
                passRate: Number(passRate) || DEFAULT_PASS_RATE,
                note: note.trim(),
                proctored,
            });
            // Clear the draft marker so the published template no longer shows up under "resume".
            try {
                await updateExamBaseWizardStep(examBaseId, null);
            } catch {
                /* best effort */
            }
            setPublishedInfo({
                title: examName.trim(),
                questionCount: questions.length,
                durationMinutes: Number(durationMinutes) || DEFAULT_DURATION,
                startDisplay: formatDateTime(startLocal),
                studentCount: assigned.length,
            });
            setDone(true);
        } catch (e) {
            // Schedule clashes come back with a stable code + the full conflict list — show the
            // detailed popup instead of a one-line banner so the user sees exactly who's busy.
            const data = e?.response?.data;
            if (data?.code === "STUDENT_SCHEDULE_CONFLICT" && Array.isArray(data.conflicts)) {
                setScheduleConflicts(data.conflicts);
            } else {
                setError(errMsg(e, t("pages.examWizard.publishError")));
            }
        } finally {
            setAdvancing(false);
        }
    };

    // Close the conflict popup and jump the user straight to the start-time field to fix it.
    const handleChangeTimeFromConflict = () => {
        setScheduleConflicts(null);
        requestAnimationFrame(() => {
            const el = startTimeRef.current;
            if (el) {
                el.scrollIntoView({behavior: "smooth", block: "center"});
                el.focus();
            }
        });
    };

    // ── Step 2 helpers ──────────────────────────────────────────────────────
    const openManualCreate = () => {
        setEditingQuestion(null);
        setManualOpen(true);
    };
    const openManualEdit = (q) => {
        setEditingQuestion(q);
        setManualOpen(true);
    };
    const handleRemoveQuestion = async (q) => {
        try {
            await deleteQuestionFromExamBase(examBaseId, q.id);
            toast.success(t("pages.examWizard.step2.removed"));
            fetchQuestions();
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.saveError")));
        }
    };

    // ── Step 4 helpers ──────────────────────────────────────────────────────
    const toggleStudent = (sn, student) => {
        const key = String(sn);
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
        // Remember the label alongside the id: after a search the row may leave the loaded page, and
        // a selection you can't see or undo is worse than no selection at all.
        setSelectedNames((prev) => {
            const next = new Map(prev);
            if (next.has(key)) next.delete(key);
            else next.set(key, student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : key);
            return next;
        });
    };

    const clearSelection = () => {
        setSelected(new Set());
        setSelectedNames(new Map());
    };

    // "Select all matching" asks the server for the numbers of every student matching the current
    // search — not just the pages scrolled so far — so it means the same thing it did when the step
    // loaded the whole roster. Names of unloaded students fall back to their number on the chip.
    const selectAllMatching = async () => {
        if (selectAllBusy) return;
        setSelectAllBusy(true);
        try {
            const numbers = await getStudentNumbers({search: studentSearchTerm});
            const list = Array.isArray(numbers) ? numbers.map(String) : [];
            const byNumber = new Map(students.map((s) => [String(s.studentNumber), s]));
            setSelected((prev) => new Set([...prev, ...list]));
            setSelectedNames((prev) => {
                const next = new Map(prev);
                list.forEach((sn) => {
                    if (next.has(sn)) return;
                    const row = byNumber.get(sn);
                    next.set(sn, row ? `${row.firstName || ""} ${row.lastName || ""}`.trim() : sn);
                });
                return next;
            });
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.studentsLoadError")));
        } finally {
            setSelectAllBusy(false);
        }
    };

    const handleStudentFormChange = (e) => {
        const {name, value} = e.target;
        setStudentForm((prev) => ({...prev, [name]: value}));
    };

    const handleStudentCreateSubmit = async (e) => {
        e.preventDefault();
        setStudentFormError("");
        if (!studentForm.firstName.trim() || !studentForm.lastName.trim()) {
            setStudentFormError(t("pages.students.firstAndLastNameRequired"));
            return;
        }
        if (!studentForm.email.trim()) {
            setStudentFormError(t("pages.students.emailRequired"));
            return;
        }
        // Only the name/email fields are collected here; everything else stays optional and can be
        // filled in later from the students panel.
        const phoneLocal = (studentForm.phone || "").trim();
        if (phoneLocal && !PHONE_LOCAL_REGEX.test(phoneLocal)) {
            setStudentFormError(t("validation.phoneFormat"));
            return;
        }
        setStudentSaving(true);
        try {
            const result = await createStudent({
                firstName: studentForm.firstName.trim(),
                lastName: studentForm.lastName.trim(),
                email: studentForm.email.trim(),
                studentNumber: "",
                phone: phoneLocal ? `+994${phoneLocal}` : null,
                nationalId: studentForm.nationalId?.trim() || null,
                birthDate: studentForm.birthDate || null,
                gender: studentForm.gender || null,
                address: studentForm.address?.trim() || null,
            });
            // The create endpoint returns the new student (with its server-generated number), so it
            // can be pushed onto the list and selected directly — no refetch of the roster.
            const created = result?.data?.student;
            const createdNumber = created?.studentNumber;
            if (createdNumber) {
                const sn = String(createdNumber);
                setStudents((prev) => (prev.some((s) => String(s.studentNumber) === sn) ? prev : [created, ...prev]));
                setStudentsTotal((n) => n + 1);
                setSelected((prev) => new Set(prev).add(sn));
                setSelectedNames((prev) => new Map(prev).set(
                    sn, `${created.firstName || ""} ${created.lastName || ""}`.trim() || sn));
            }
            toast.success(t("pages.examWizard.step4.studentCreated"));
            setStudentModalOpen(false);
            setStudentForm(EMPTY_STUDENT);
        } catch (e) {
            setStudentFormError(errMsg(e, t("pages.students.operationFailed")));
        } finally {
            setStudentSaving(false);
        }
    };

    // ── Exit / discard ──────────────────────────────────────────────────────
    const handleSaveAndExit = () => {
        if (examBaseId) toast.success(t("pages.examWizard.savedDraft"));
        navigate("/exams/base");
    };

    const handleConfirmDiscard = async () => {
        setDiscarding(true);
        try {
            if (examBaseId) await deleteExamBase(examBaseId);
            toast.success(t("pages.examWizard.discarded"));
            navigate("/exams/base");
        } catch (e) {
            setError(errMsg(e, t("pages.examWizard.saveError")));
            setDiscardOpen(false);
        } finally {
            setDiscarding(false);
        }
    };

    const handleCopyLink = async () => {
        const link = `${window.location.origin}/exams/my`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success(t("pages.examWizard.success.linkCopied"));
        } catch {
            toast.info(link);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    if (bootLoading) {
        return (
            <div className={styles.page}>
                <div className={styles.shell}>
                    <div className={styles.loaderWrap}>
                        <Loader/>
                    </div>
                </div>
            </div>
        );
    }

    if (done && publishedInfo) {
        const studentLink = `${window.location.origin}/exams/my`;
        return (
            <div className={styles.page}>
                <div className={styles.shell}>
                    <div className={styles.card}>
                        <div className={styles.success}>
                            <div className={styles.successIcon}>
                                <FiCheckCircle size={36}/>
                            </div>
                            <h1 className={styles.successTitle}>{t("pages.examWizard.success.title")}</h1>
                            <p className={styles.successSubtitle}>
                                {t("pages.examWizard.success.subtitle", {name: publishedInfo.title})}
                            </p>

                            <div className={styles.detailGrid}>
                                <div className={styles.detailCell}>
                                    <div className={styles.detailLabel}>{t("pages.examWizard.success.questions")}</div>
                                    <div className={styles.detailValue}>{publishedInfo.questionCount}</div>
                                </div>
                                <div className={styles.detailCell}>
                                    <div className={styles.detailLabel}>{t("pages.examWizard.success.duration")}</div>
                                    <div className={styles.detailValue}>
                                        {t("pages.examWizard.success.minutes", {count: publishedInfo.durationMinutes})}
                                    </div>
                                </div>
                                <div className={styles.detailCell}>
                                    <div className={styles.detailLabel}>{t("pages.examWizard.success.start")}</div>
                                    <div className={styles.detailValue}>{publishedInfo.startDisplay}</div>
                                </div>
                                <div className={styles.detailCell}>
                                    <div className={styles.detailLabel}>{t("pages.examWizard.success.students")}</div>
                                    <div className={styles.detailValue}>{publishedInfo.studentCount}</div>
                                </div>
                            </div>

                            <div className={styles.linkBox}>
                                <div className={styles.linkBoxLabel}>{t("pages.examWizard.success.linkLabel")}</div>
                                <div className={styles.linkRow}>
                                    <input className={styles.linkInput} readOnly value={studentLink}/>
                                    <button type="button" className={styles.copyBtn} onClick={handleCopyLink}>
                                        <FiCopy size={15}/> {t("common.copy")}
                                    </button>
                                </div>
                                <div className={styles.linkHint}>{t("pages.examWizard.success.linkHint")}</div>
                            </div>

                            <button type="button" className={styles.primaryBtn} onClick={() => navigate("/exams/base")}>
                                {t("pages.examWizard.success.done")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.shell}>
                <div className={styles.topbar}>
                    <span className={styles.brandMark}>
                        <img src="/gredus-logo.svg" alt="Gredus"/>
                        {t("pages.examWizard.title")}
                    </span>
                    <div className={styles.topbarActions}>
                        {step === 1 && !examBaseId && (
                            <button type="button" className={styles.ghostLink} onClick={() => navigate("/exams/base")}>
                                {t("pages.examWizard.useAdvanced")}
                            </button>
                        )}
                        <button type="button" className={styles.ghostLink} onClick={handleSaveAndExit}>
                            {t("pages.examWizard.saveAndExit")}
                        </button>
                        {examBaseId && (
                            <button
                                type="button"
                                className={`${styles.ghostLink} ${styles.ghostLinkDanger}`}
                                onClick={() => setDiscardOpen(true)}
                            >
                                {t("pages.examWizard.discard")}
                            </button>
                        )}
                    </div>
                </div>

                <WizardProgress step={step} labels={stepLabels}/>

                <div className={styles.card}>
                    {/* ── Step 1: Name ───────────────────────────────────────── */}
                    {step === 1 && (
                        <>
                            <div className={styles.stepHeader}>
                                <h1 className={styles.stepTitle}>{t("pages.examWizard.step1.title")}</h1>
                                <p className={styles.stepHint}>{t("pages.examWizard.step1.hint")}</p>
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>{t("pages.examWizard.step1.examName")}</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={examName}
                                    onChange={(e) => setExamName(e.target.value)}
                                    placeholder={t("pages.examWizard.step1.examNamePlaceholder")}
                                    maxLength={100}
                                />
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>{t("pages.examWizard.step1.topics")}</label>

                                {selectedTopics.length > 0 && (
                                    <div className={styles.topicChips}>
                                        {selectedTopics.map((tp) => (
                                            <span key={tp.id} className={styles.topicChip}>
                                                <FiCheck size={12}/>
                                                {tp.parentName ? `${tp.parentName} › ${tp.title}` : tp.title}
                                                <button
                                                    type="button"
                                                    className={styles.topicChipRemove}
                                                    onClick={() => removeTopic(tp.id)}
                                                    aria-label={t("pages.examWizard.step1.removeTopic")}
                                                >
                                                    <FiX size={12}/>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <TopicSelector
                                    value=""
                                    onChange={(id, topic) => addTopic(id, topic)}
                                    topics={subjectOptions}
                                    onTopicCreated={(newTopic) =>
                                        setSubjectOptions((prev) => [...prev, newTopic])
                                    }
                                    placeholder={t("pages.examWizard.step1.addTopicPlaceholder")}
                                    onAddAll={(items) => items.forEach((tp) => addTopic(tp.id, tp))}
                                    selectedIds={selectedTopics.map((tp) => tp.id)}
                                />
                                <div className={styles.helpText}>{t("pages.examWizard.step1.topicsHelp")}</div>
                            </div>
                        </>
                    )}

                    {/* ── Step 2: Questions ──────────────────────────────────── */}
                    {step === 2 && (
                        <>
                            <div className={styles.stepHeader}>
                                <h1 className={styles.stepTitle}>{t("pages.examWizard.step2.title")}</h1>
                                <p className={styles.stepHint}>{t("pages.examWizard.step2.hint")}</p>
                            </div>

                            <div className={styles.pathGrid}>
                                <button
                                    type="button"
                                    className={`${styles.pathCard} ${styles.pathCardPrimary}`}
                                    onClick={() => setAiOpen(true)}
                                >
                                    <span className={styles.pathIcon}><FiCpu size={18}/></span>
                                    <span className={styles.pathTitle}>{t("pages.examWizard.step2.aiTitle")}</span>
                                    <span className={styles.pathDesc}>{t("pages.examWizard.step2.aiDesc")}</span>
                                </button>
                                <button type="button" className={styles.pathCard} onClick={() => setBankOpen(true)}>
                                    <span className={styles.pathIcon}><FiDatabase size={18}/></span>
                                    <span className={styles.pathTitle}>{t("pages.examWizard.step2.bankTitle")}</span>
                                    <span className={styles.pathDesc}>{t("pages.examWizard.step2.bankDesc")}</span>
                                </button>
                                <button type="button" className={styles.pathCard} onClick={openManualCreate}>
                                    <span className={styles.pathIcon}><FiEdit3 size={18}/></span>
                                    <span className={styles.pathTitle}>{t("pages.examWizard.step2.manualTitle")}</span>
                                    <span className={styles.pathDesc}>{t("pages.examWizard.step2.manualDesc")}</span>
                                </button>
                            </div>

                            <div className={styles.listHeader}>
                                <span className={styles.listTitle}>{t("pages.examWizard.step2.added")}</span>
                                <span className={styles.countPill}>
                                    {t("pages.examWizard.step2.count", {count: questions.length})}
                                </span>
                            </div>

                            {questionsLoading ? (
                                <div className={styles.loaderWrap}><Loader/></div>
                            ) : questions.length === 0 ? (
                                <div className={styles.emptyHint}>{t("pages.examWizard.step2.empty")}</div>
                            ) : (
                                <div className={styles.questionList}>
                                    {questions.map((q, i) => {
                                        // Prefer the question's own subject/topic (returned by the API as
                                        // parentName/topicTitle). Fall back to resolving from the selected
                                        // topics by subjectId for a just-added question not yet refetched.
                                        let subjLabel = [q.parentName, q.topicTitle].filter(Boolean).join(" › ");
                                        if (!subjLabel) {
                                            const tp = selectedTopics.find((s) => String(s.id) === String(q.subjectId));
                                            subjLabel = tp ? (tp.parentName ? `${tp.parentName} › ${tp.title}` : tp.title) : null;
                                        }
                                        return (
                                        <div className={styles.questionItem} key={q.id || i}>
                                            <span className={styles.qIndex}>{i + 1}</span>
                                            <div className={styles.qBody}>
                                                <div className={styles.qText}>
                                                    <QuestionContent text={q.text}/>
                                                </div>
                                                <div className={styles.qMeta}>
                                                    {q.type === "OPEN"
                                                        ? t("pages.examWizard.step2.openType")
                                                        : t("pages.examWizard.step2.optionCount", {
                                                            count: q.options?.length || 0,
                                                        })}
                                                </div>
                                                {subjLabel && (
                                                    <span style={{
                                                        display: "inline-block",
                                                        marginTop: 6,
                                                        padding: "2px 10px",
                                                        background: "#eff6ff",
                                                        color: "#2563eb",
                                                        border: "1px solid #bfdbfe",
                                                        borderRadius: 999,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                    }}>{subjLabel}</span>
                                                )}
                                            </div>
                                            <div className={styles.qActions}>
                                                <button
                                                    type="button"
                                                    className={styles.iconBtn}
                                                    title={t("common.edit")}
                                                    onClick={() => openManualEdit(q)}
                                                >
                                                    <FiEdit3 size={16}/>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                                    title={t("pages.examWizard.step2.remove")}
                                                    onClick={() => handleRemoveQuestion(q)}
                                                >
                                                    <FiTrash2 size={16}/>
                                                </button>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Step 3: Settings ───────────────────────────────────── */}
                    {step === 3 && (
                        <>
                            <div className={styles.stepHeader}>
                                <h1 className={styles.stepTitle}>{t("pages.examWizard.step3.title")}</h1>
                                <p className={styles.stepHint}>{t("pages.examWizard.step3.hint")}</p>
                            </div>

                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <label className={styles.label}>{t("pages.examWizard.step3.duration")}</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={300}
                                        className={styles.input}
                                        value={durationMinutes}
                                        onChange={(e) => setDurationMinutes(e.target.value)}
                                    />
                                    {Number(durationMinutes) > MAX_EXAM_DURATION_MINUTES ? (
                                        <span style={{display: "block", marginTop: 4, fontSize: 12, color: "#dc2626"}}>
                                            {t("common.durationMax")}
                                        </span>
                                    ) : formatMinutesAsHours(durationMinutes, t) ? (
                                        <span style={{display: "block", marginTop: 4, fontSize: 12, color: "#6b7280"}}>
                                            = {formatMinutesAsHours(durationMinutes, t)}
                                        </span>
                                    ) : null}
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>{t("pages.examWizard.step3.passRate")}</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={100}
                                        className={styles.input}
                                        value={passRate}
                                        onChange={(e) => setPassRate(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className={styles.toggleRow}>
                                <input type="checkbox" checked readOnly disabled/>
                                <div>
                                    <div className={styles.toggleText}>{t("pages.examWizard.step3.shuffle")}</div>
                                    <div className={styles.toggleHint}>{t("pages.examWizard.step3.shuffleHint")}</div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── Step 4: Schedule & audience ────────────────────────── */}
                    {step === 4 && (
                        <>
                            <div className={styles.stepHeader}>
                                <h1 className={styles.stepTitle}>{t("pages.examWizard.step4.title")}</h1>
                                <p className={styles.stepHint}>{t("pages.examWizard.step4.hint")}</p>
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>{t("pages.examWizard.step4.startTime")}</label>
                                <input
                                    ref={startTimeRef}
                                    type="datetime-local"
                                    className={styles.input}
                                    value={startLocal}
                                    onChange={(e) => setStartLocal(e.target.value)}
                                />
                                <div className={styles.helpText}>{t("pages.examWizard.step4.startHelp")}</div>
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>{t("pages.examWizard.step4.note")}</label>
                                <textarea
                                    className={styles.textarea}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    maxLength={100}
                                    placeholder={t("pages.examWizard.step4.notePlaceholder")}
                                />
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {t("pages.examWizard.step4.proctored")}
                                </label>
                                <div className={styles.toggleRow}>
                                    <input
                                        type="checkbox"
                                        checked={proctored}
                                        onChange={(e) => setProctored(e.target.checked)}
                                    />
                                    <div>
                                        <div className={styles.toggleText}>{t("pages.examWizard.step4.proctoredText")}</div>
                                        <div className={styles.toggleHint}>{t("pages.examWizard.step4.proctoredHint")}</div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>{t("pages.examWizard.step4.audience")}</label>
                                <div className={styles.studentsBox}>
                                    <div className={styles.studentsBoxHeader}>
                                        <span className={styles.studentsBoxTitle}>
                                            {t("pages.examWizard.step4.selectedCount", {count: selected.size})}
                                        </span>
                                        <div className={styles.studentsBoxActions}>
                                            {studentsTotal > 0 && (
                                                <button
                                                    type="button"
                                                    className={styles.selectAllBtn}
                                                    onClick={selectAllMatching}
                                                    disabled={selectAllBusy}
                                                >
                                                    {t("pages.examWizard.step4.selectAllMatching", {count: studentsTotal})}
                                                </button>
                                            )}
                                            {selected.size > 0 && (
                                                <button
                                                    type="button"
                                                    className={styles.selectAllBtn}
                                                    onClick={clearSelection}
                                                >
                                                    {t("pages.examWizard.step4.deselectAll")}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Selected students stay pinned here, so a search or scroll can
                                        never hide what is already chosen. */}
                                    {selected.size > 0 && (
                                        <div className={styles.selectedChips}>
                                            {[...selectedNames.entries()].map(([sn, name]) => (
                                                <span key={sn} className={styles.selectedChip}>
                                                    {name || sn}
                                                    <button
                                                        type="button"
                                                        className={styles.selectedChipRemove}
                                                        onClick={() => toggleStudent(sn)}
                                                        aria-label={t("pages.examWizard.step4.removeStudent")}
                                                    >
                                                        <FiX size={12}/>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <input
                                        type="text"
                                        className={styles.searchInput}
                                        value={studentSearch}
                                        onChange={(e) => setStudentSearch(e.target.value)}
                                        placeholder={t("pages.examWizard.step4.searchStudents")}
                                    />

                                    {students.length === 0 && !studentsLoading ? (
                                        <div className={styles.emptyHint}>{t("pages.examWizard.step4.noStudents")}</div>
                                    ) : (
                                        <div className={styles.studentsList} onScroll={handleStudentsScroll}>
                                            {students.map((s) => {
                                                const sn = String(s.studentNumber);
                                                return (
                                                    <label className={styles.studentRow} key={sn}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.has(sn)}
                                                            onChange={() => toggleStudent(sn, s)}
                                                        />
                                                        <span>
                                                            {s.firstName} {s.lastName}{" "}
                                                            <span className={styles.studentTag}>· {sn}</span>
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                            {studentsLoading && (
                                                <div className={styles.studentsListNote}>{t("common.loading")}</div>
                                            )}
                                            {!studentsLoading && studentsHasMore && (
                                                <div className={styles.studentsListNote}>
                                                    {t("pages.examWizard.step4.scrollForMore")}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {canManageStudents && (
                                    <div className={styles.addStudentsBar}>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={() => {
                                                setStudentForm(EMPTY_STUDENT);
                                                setStudentFormError("");
                                                setStudentModalOpen(true);
                                            }}
                                        >
                                            <FiUserPlus size={15}/> {t("pages.examWizard.step4.createStudent")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {softError && <div className={styles.softMessage}>{softError}</div>}
                    {error && <div className={styles.error}>{error}</div>}

                    {/* ── Footer navigation ──────────────────────────────────── */}
                    <div className={styles.footer}>
                        {step > 1 && (
                            <button type="button" className={styles.backBtn} onClick={goBack} disabled={advancing}>
                                <FiArrowLeft size={16}/> {t("common.back")}
                            </button>
                        )}
                        <div className={styles.footerRight}>
                            {step === 1 && (
                                <button type="button" className={styles.primaryBtn} onClick={handleStep1Next} disabled={advancing}>
                                    {advancing ? <span className={styles.btnSpinner}/> : null}
                                    {t("common.continue")} <FiArrowRight size={16}/>
                                </button>
                            )}
                            {step === 2 && (
                                <button type="button" className={styles.primaryBtn} onClick={handleStep2Next} disabled={advancing}>
                                    {advancing ? <span className={styles.btnSpinner}/> : null}
                                    {t("common.continue")} <FiArrowRight size={16}/>
                                </button>
                            )}
                            {step === 3 && (
                                <>
                                    <button
                                        type="button"
                                        className={styles.secondaryBtn}
                                        onClick={() => handleStep3Next(true)}
                                        disabled={advancing}
                                    >
                                        {t("pages.examWizard.step3.continueDefaults")}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.primaryBtn}
                                        onClick={() => handleStep3Next(false)}
                                        disabled={advancing}
                                    >
                                        {advancing ? <span className={styles.btnSpinner}/> : null}
                                        {t("common.continue")} <FiArrowRight size={16}/>
                                    </button>
                                </>
                            )}
                            {step === 4 && (
                                <button type="button" className={styles.primaryBtn} onClick={handlePublish} disabled={advancing}>
                                    {advancing ? <span className={styles.btnSpinner}/> : null}
                                    {t("pages.examWizard.step4.publish")}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Reused modals ──────────────────────────────────────────────── */}
            <QuestionFormModal
                isOpen={manualOpen}
                onClose={() => {
                    setManualOpen(false);
                    setEditingQuestion(null);
                }}
                examBaseId={examBaseId}
                examSubjects={selectedTopics}
                mode={editingQuestion ? "edit" : "create"}
                initialQuestion={editingQuestion}
                onSuccess={() => {
                    setManualOpen(false);
                    setEditingQuestion(null);
                    fetchQuestions();
                }}
            />

            <AiQuestionGenerateModal
                isOpen={aiOpen}
                onClose={() => setAiOpen(false)}
                examBaseId={examBaseId}
                onSuccess={() => {
                    setAiOpen(false);
                    fetchQuestions();
                }}
            />

            <SelectFromQuestionBankModal
                isOpen={bankOpen}
                onClose={() => setBankOpen(false)}
                examBaseId={examBaseId}
                onSuccess={() => {
                    setBankOpen(false);
                    fetchQuestions();
                }}
            />

            <StudentFormModal
                isOpen={studentModalOpen}
                mode="create"
                basicOnly
                values={studentForm}
                onChange={handleStudentFormChange}
                onClose={() => setStudentModalOpen(false)}
                onSubmit={handleStudentCreateSubmit}
                saving={studentSaving}
                error={studentFormError}
            />

            <ConfirmDeleteModal
                isOpen={discardOpen}
                onConfirm={handleConfirmDiscard}
                onCancel={() => setDiscardOpen(false)}
                message={t("pages.examWizard.discardConfirm")}
                disabled={discarding}
            />

            <ExamConflictModal
                isOpen={!!scheduleConflicts}
                conflicts={scheduleConflicts || []}
                onClose={() => setScheduleConflicts(null)}
                onChangeTime={handleChangeTimeFromConflict}
            />
        </div>
    );
};

export default ExamWizardPage;
